import React from "react";
import { type GetStaticProps, type NextPage } from "next";
import { type Page as SanityPage, type Banner, getAnnouncementBanner, getPage } from "lib/sanity";
import { getPageMetaImage } from "utils";
import { HalfHero } from "components/half-hero";
import { Section, sectionSpacingY } from "components/section";
import { Page } from "components/page";
import { CenteredQuote } from "components/quotes";
import { DemoEmbedHalf } from "components/demo-embed";
import { PairImage } from "components/pair-image";
import { StackedLogos } from "components/logos";
import { RadialGradient } from "ui/radial-gradient";
import { SecurityFeatures } from "components/content";
import { integrationsCount } from "../../constants/integrations";
import heroImage from "../../../public/assets/solutions/financial-services/hero.png";
import firstPairImage from "../../../public/assets/solutions/financial-services/pair-1.png";
import secondPairImage from "../../../public/assets/solutions/financial-services/pair-2.png";
import thirdPairImage from "../../../public/assets/solutions/financial-services/pair-3.png";

type PageProps = {
  page: SanityPage;
  announcementBanner?: Banner;
};

const FinancialServices: NextPage<Readonly<PageProps>> = ({ page, announcementBanner }) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <HalfHero
        hideSecondary
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        image={heroImage}
        imageRatio={5 / 4}
        imageObjectFit="contain"
        containerProps={{ gap: { base: 12, xl: 16 } }}
        backgroundGradient={
          <>
            <RadialGradient
              variant="cyan"
              bottom={{ base: "60%", md: "70%" }}
              right={{ base: "40%", md: "12%" }}
              boxSize={{ base: "70%", md: "80%" }}
              opacity={{ base: 0.4, xl: 0.6 }}
            />
            <RadialGradient
              variant="lightspeed"
              bottom="76%"
              right={{ base: "0%", md: "-20%" }}
              boxSize={{ base: "70%", md: "80%" }}
              opacity={{ base: 0.2, md: 0.4 }}
            />
            <RadialGradient
              variant="mint"
              bottom={{ base: "70%", md: "10%" }}
              right={{ base: "-50%", md: "-110%" }}
              boxSize={{ base: "120%", md: "160%" }}
              opacity={{ base: 0.4, xl: 0.6 }}
            />
          </>
        }
      />

      {page.customers && (
        <StackedLogos
          title="Trusted by teams at"
          customerRows={[page.customers]}
          mobileCustomers={page.customers}
          my={16}
        />
      )}

      <SecurityFeatures />

      <DemoEmbedHalf />

      <Section>
        <PairImage
          align="right"
          image={firstPairImage}
          title="Fully secure Customer Data Platform"
          description="Store your data in your existing data infrastructure. Hightouch is compliant with all major security laws, allowing you to build best-in-class customer experiences, without storing data with a third-party vendor."
          actionText="Learn more about our security features"
          actionUrl="/platform/security"
        />
      </Section>

      <Section>
        <PairImage
          align="left"
          image={secondPairImage}
          title="Hyper-personalize customer journeys"
          description={`Leverage your customer data to personalize every customer interaction across your website, email, ads, and ${integrationsCount}+ other Hightouch destinations. Media recommendations made easy.`}
        />
      </Section>

      <Section>
        <PairImage
          align="right"
          image={thirdPairImage}
          title="Bridge the gap between data and marketing teams"
          description="Built for marketing teams and loved by data teams. With Hightouch, marketing teams get access to the data they need in the tools they use everyday. Data teams maintain full observability and control in your data infrastructure."
        />
      </Section>

      <Section my={0} pb={sectionSpacingY}>
        <CenteredQuote
          quoteText="This might be one of the greatest inventions for technical marketers since the advent of legacy CDPs back in 2015."
          author={{ name: "Austin Hay", job: "Head of Marketing Technology" }}
          company={{
            name: "Ramp",
            logo: "https://cdn.sanity.io/images/pwmfmi47/production/acaf90882a1ded8a3796cc3972219355a42d009c-270x72.svg",
          }}
          cta={{ href: "/customers/ramp", text: "Read customer story" }}
        />
      </Section>
    </Page>
  );
};

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, announcementBanner] = await Promise.all([
    getPage("/solutions/financial-services"),
    getAnnouncementBanner(),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      announcementBanner,
    },
    revalidate: 60,
  };
};

export default FinancialServices;
